import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { api, type FieldTestInfo, type FieldTestResultView, type FieldTestShotView, type FieldTestStatus, type FieldTestsView } from '../../api/client';
import { Button, Card, Chip, Empty, Kpi, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { shortWhen, type Tone } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { picturesIn, shrinkScreenshot } from '../../lib/screenshots';
import { useData } from '../../lib/useData';
import { GatewayRun } from './GatewayRun';

/**
 * Stitch Admin → Field tests (Joey, Nov 1 2026; docs/field-tests.md): the tests only the real world
 * can answer (a real desk phone, the carrier, a strict network), each with its user story, steps,
 * what should happen and what to write down. Every result is kept; Export gives one JSON file to
 * send to Claude, or a zip with the JSON and every screenshot (Joey, Nov 3 2026: paste, drop or
 * choose screenshots on a test; they're kept with its result). Copy summary gives one test's newest
 * result as text for its Teampace task (3124); `?test=<id>` (from Stitch Admin → Updates) opens that test.
 */

const STATUS: Record<FieldTestStatus, { label: string; tone: Tone }> = {
  passed: { label: 'Passed', tone: 'ok' },
  failed: { label: 'Failed', tone: 'crit' },
  blocked: { label: 'Couldn’t try', tone: 'warn' },
  skipped: { label: 'Skipped', tone: 'neutral' },
  not_run: { label: 'Not done', tone: 'neutral' }
};

type Show = 'todo' | 'problems' | 'done' | 'all';
const SHOW: [Show, string][] = [
  ['todo', 'To do'],
  ['problems', 'Problems'],
  ['done', 'Done'],
  ['all', 'All']
];

const statusOf = (t: FieldTestInfo): FieldTestStatus => t.result?.status ?? 'not_run';
const fits = (t: FieldTestInfo, show: Show) => {
  const s = statusOf(t);
  if (show === 'todo') return s === 'not_run' || s === 'blocked';
  if (show === 'problems') return s === 'failed' || s === 'blocked';
  if (show === 'done') return s === 'passed' || s === 'skipped' || s === 'failed';
  return true;
};

/** `backticks` show as code: commands and the words on screen. */
function Words({ text }: { text: string }) {
  const parts = text.split('`');
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <code key={i} className="field-test-code">
            {p}
          </code>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        )
      )}
    </>
  );
}

function byArea(list: FieldTestInfo[], areas: string[]): [string, FieldTestInfo[]][] {
  return areas.map((a): [string, FieldTestInfo[]] => [a, list.filter((t) => t.area === a)]).filter(([, l]) => l.length > 0);
}

export function AdminFieldTests() {
  const data = useData(() => api.fieldTests(), [], { live: false });
  const [params] = useSearchParams();
  // Opened from Updates (3124): that test, whatever its result.
  const focus = params.get('test');
  const [view, setView] = useState<FieldTestsView | null>(null);
  const [show, setShow] = useState<Show>(focus ? 'all' : 'todo');
  // Tests recorded on this view stay where they are, with their new result, until the view changes
  // (Joey, Nov 3 2026: on To do, Passed and Failed made a test vanish, so nothing seemed to be saved).
  const [kept, setKept] = useState<ReadonlySet<string>>(() => new Set());
  const choose = (k: Show) => {
    setShow(k);
    setKept(new Set());
  };
  const [toast, setToast] = useState('');
  useEffect(() => {
    if (data.data) setView(data.data);
  }, [data.data]);

  const tests = view?.tests ?? [];
  const ready = useMemo(() => tests.filter((t) => t.ready), [tests]);
  const later = useMemo(() => tests.filter((t) => !t.ready), [tests]);

  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!view) return <Loading label="Loading field tests" />;

  const count = (s: FieldTestStatus, list = ready) => list.filter((t) => statusOf(t) === s).length;
  const saved = (id: string, result: FieldTestResultView, same = false) => {
    setView((v) => (v ? { ...v, tests: v.tests.map((t) => (t.id === id ? { ...t, result, results: t.results + (same ? 0 : 1), waitingShots: same ? t.waitingShots : [] } : t)) } : v));
    setKept((k) => new Set(k).add(id));
    const t = tests.find((x) => x.id === id);
    setToast(same ? `Screenshot added to “${t?.title ?? id}”.` : `“${t?.title ?? id}” saved as ${STATUS[result.status].label.toLowerCase()}.`);
  };
  const shownNow = ready.filter((t) => fits(t, show) || kept.has(t.id));
  const shownLater = later.filter((t) => fits(t, show) || kept.has(t.id));

  return (
    <div className="page field-tests">
      <PageHeader
        title="Field tests"
        actions={
          <>
            <a className="btn secondary" href={api.fieldTestsExportUrl()} download>
              Export results (JSON)
            </a>
            <a className="btn primary" href={api.fieldTestsZipUrl()} download>
              <Icon name="download" size={17} strokeWidth={2.2} />
              Export with screenshots (zip)
            </a>
          </>
        }
      />
      <p className="muted settings-lede">
        Tests only the real world can answer: a real desk phone, the carrier, real money, a strict network. Each one has who it’s for, the steps, what should happen and what
        to write down. Paste, drop or choose screenshots on a test and they’re kept with its result. Every result is kept with the build it was tried on. When you’ve done some, export them (the zip has the screenshots too) and send the file to Claude in the chat. Running{' '}
        {view.running.version} ({view.running.build}).
      </p>
      <div className="kpis tests-kpis">
        <Kpi small label="Passed" value={count('passed')} foot={`of ${ready.length} ready now`} />
        <Kpi small label="Failed" value={count('failed')} foot="Did the wrong thing" />
        <Kpi small label="Couldn’t try" value={count('blocked')} foot="Something was missing" />
        <Kpi small label="Not done" value={count('not_run')} foot={`${ready.filter((t) => statusOf(t) === 'not_run').reduce((n, t) => n + t.minutes, 0)} minutes or so`} />
        <Kpi small label="After the next release" value={later.length} foot={later.length ? `Need ${[...new Set(later.map((t) => t.needs))].join(', ')}` : 'None waiting'} />
      </div>
      <div className="segmented small field-tests-show" role="group" aria-label="Show">
        {SHOW.map(([k, label]) => (
          <button key={k} type="button" className={show === k ? 'on' : ''} aria-pressed={show === k} onClick={() => choose(k)}>
            {label}
          </button>
        ))}
      </div>

      <Card title="Ready now">
        {shownNow.length === 0 ? (
          <Empty title="Nothing here">{show === 'todo' ? 'Every test that runs on this build has a result.' : 'No tests match.'}</Empty>
        ) : (
          byArea(shownNow, view.areas).map(([area, list]) => <Area key={area} area={area} list={list} onSaved={saved} onToast={setToast} focus={focus} />)
        )}
      </Card>
      {later.length > 0 && (
        <Card title="After the next release is deployed">
          <p className="card-body muted tests-intro">These need a newer Stitch than the one running. Read them ahead; record them once it’s deployed.</p>
          {shownLater.length === 0 ? <Empty title="Nothing here">No tests match.</Empty> : byArea(shownLater, view.areas).map(([area, list]) => <Area key={area} area={area} list={list} onSaved={saved} onToast={setToast} focus={focus} />)}
        </Card>
      )}
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </div>
  );
}

interface RowProps {
  /** A result saved, or (`same`) a screenshot added to the newest one. */
  onSaved: (id: string, r: FieldTestResultView, same?: boolean) => void;
  onToast: (msg: string) => void;
  /** The test opened from Updates (?test=), if any. */
  focus: string | null;
}

function Area({ area, list, onSaved, onToast, focus }: { area: string; list: FieldTestInfo[] } & RowProps) {
  return (
    <section className="tests-area">
      <h3 className="tests-area-title">{area}</h3>
      <ul className="tests-list">
        {list.map((t) => (
          <FieldTestRow key={t.id} t={t} onSaved={onSaved} onToast={onToast} focus={focus} />
        ))}
      </ul>
    </section>
  );
}

function FieldTestRow({ t, onSaved, onToast, focus }: { t: FieldTestInfo } & RowProps) {
  const r = t.result;
  const [open, setOpen] = useState(focus === t.id);
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focus === t.id) row.current?.scrollIntoView({ block: 'start' });
  }, [focus, t.id]);
  // Copy summary (3124): the newest result as text for Teampace; shown to copy by hand when the browser won't.
  const [summary, setSummary] = useState('');
  const [copying, setCopying] = useState(false);
  // Run here (3131): the gateway's tools beside the steps; the log from when they opened goes with the result.
  const [running, setRunning] = useState(false);
  const [logSince, setLogSince] = useState<number | null>(null);
  const [expected, setExpected] = useState<boolean[]>(() => t.expected.map((_, i) => r?.expected[i] === true));
  const [answers, setAnswers] = useState<Record<string, string>>(() => ({ ...(r?.answers ?? {}) }));
  const [notes, setNotes] = useState(r?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<FieldTestResultView[] | null>(null);
  // Screenshots for the result being written (kept on the server, waiting until it's saved).
  const [shots, setShots] = useState<FieldTestShotView[]>(() => t.waitingShots ?? []);
  const [adding, setAdding] = useState(0);
  const [shotError, setShotError] = useState('');
  const [dropping, setDropping] = useState(false);
  const pickNew = useRef<HTMLInputElement>(null);
  const pickSaved = useRef<HTMLInputElement>(null);
  const s = STATUS[statusOf(t)];

  async function save(status: FieldTestStatus) {
    setBusy(true);
    setError('');
    try {
      const out = await api.saveFieldTest(t.id, { status, notes, expected, answers, shots: shots.map((x) => x.id), ...(logSince !== null ? { gatewayLogSince: logSince } : {}) });
      onSaved(t.id, out.result);
      setShots([]);
      setHistory(null);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function copySummary() {
    setCopying(true);
    setError('');
    try {
      const { text, task } = await api.fieldTestSummary(t.id);
      try {
        await navigator.clipboard.writeText(text);
        setSummary('');
        onToast(task ? `Summary copied: paste it into Teampace task ${task}.` : 'Summary copied.');
      } catch {
        setSummary(text);
        setOpen(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setOpen(true);
    } finally {
      setCopying(false);
    }
  }
  /** Shrinks each picture and keeps it: for the result being written, or (saved) the newest result. */
  async function addPictures(files: File[], to: 'new' | 'saved' = 'new') {
    if (!files.length) return;
    setShotError('');
    setOpen(true);
    for (const f of files) {
      setAdding((n) => n + 1);
      try {
        const picture = await shrinkScreenshot(f);
        const name = f.name && !/^image\.(png|jpe?g|webp)$/i.test(f.name) ? f.name.replace(/\.[^.]+$/, '') : '';
        const { shot } = await api.addFieldTestShot(t.id, picture, { ...(to === 'saved' && r ? { result: r.id } : {}), ...(name ? { name } : {}) });
        if (to === 'saved' && r) onSaved(t.id, { ...r, shots: [...(r.shots ?? []), shot] }, true);
        else setShots((x) => [...x, shot]);
      } catch (err) {
        setShotError(err instanceof Error ? err.message : String(err));
      } finally {
        setAdding((n) => n - 1);
      }
    }
  }
  async function removeShot(id: string) {
    setShotError('');
    try {
      await api.removeFieldTestShot(id);
      setShots((x) => x.filter((y) => y.id !== id));
    } catch (err) {
      setShotError(err instanceof Error ? err.message : String(err));
    }
  }
  async function loadHistory() {
    try {
      setHistory((await api.fieldTestHistory(t.id)).results);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const meta: ReactNode[] = [t.area, `about ${t.minutes} min`];
  if (t.task) meta.push(`Teampace ${t.task}`);
  if (!t.ready && t.needs) meta.push(`needs ${t.needs}`);
  const met = r ? r.expected.filter(Boolean).length : 0;

  return (
    <li
      className="tests-row field-test"
      data-test={t.id}
      ref={row}
      onPaste={(e) => {
        // A screenshot pasted anywhere in this test (⌘V right after taking it) goes with its result.
        const pics = picturesIn(e.clipboardData);
        if (!pics.length) return;
        e.preventDefault();
        void addPictures(pics);
      }}
    >
      <div className="tests-row-head">
        <Chip small tone={s.tone}>
          {s.label}
        </Chip>
        <span className="cell-stack grow">
          <span className="tests-title">{t.title}</span>
          <small className="muted">
            {r
              ? `${r.testedBy} · ${shortWhen(r.testedAt)} · ${met} of ${t.expected.length} as expected${r.shots?.length ? ` · ${r.shots.length} screenshot${r.shots.length === 1 ? '' : 's'}` : ''}${r.notes ? ` · ${r.notes.slice(0, 120)}` : ''}`
              : meta.join(' · ')}
          </small>
          {t.decides && <small className="field-test-decides">Decides: {t.decides}</small>}
        </span>
        <button type="button" className="link-btn small-print" disabled={copying} onClick={() => void copySummary()} aria-label={`Copy summary: ${t.title}`}>
          {copying ? 'Copying…' : 'Copy summary'}
        </button>
        <button type="button" className="link-btn small-print" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : r ? 'Open' : 'Do it'}
        </button>
      </div>
      {open && (
        <div className="tests-more field-test-more">
          {summary && (
            <div className="field stretch">
              <label htmlFor={`ft-${t.id}-summary`}>Summary for Teampace</label>
              <textarea id={`ft-${t.id}-summary`} className="input textarea mono" rows={8} readOnly value={summary} onFocus={(e) => e.currentTarget.select()} />
              <small className="muted">This browser wouldn’t copy it: select it and copy it by hand.</small>
            </div>
          )}
          <blockquote className="field-test-story">{t.userStory}</blockquote>
          <p>
            <strong>Why:</strong> {t.why}
          </p>
          {t.careful && (
            <Notice tone="warn" icon="alert">
              {t.careful}
            </Notice>
          )}
          <h4 className="field-test-h">Before you start</h4>
          <ul className="reg-list">
            {t.before.map((b, i) => (
              <li key={i}>
                <Words text={b} />
              </li>
            ))}
          </ul>
          {t.run === 'gateway' && (
            <div className="field-test-run">
              <Button size="small" variant={running ? 'ghost' : 'primary'} onClick={() => setRunning((r) => !r)} aria-expanded={running}>
                {running ? 'Hide the tools' : 'Run it here'}
              </Button>
              {running && <GatewayRun onStart={setLogSince} />}
            </div>
          )}
          <h4 className="field-test-h">Steps</h4>
          <ol className="tests-steps">
            {t.steps.map((step, i) => (
              <li key={i}>
                <Words text={step} />
              </li>
            ))}
          </ol>
          <h4 className="field-test-h">What should happen</h4>
          <p className="small-print muted">Tick each one that did.</p>
          <div className="field-test-expected">
            {t.expected.map((e, i) => (
              <label key={i} className="check-row">
                <input type="checkbox" checked={expected[i] ?? false} disabled={busy} onChange={(ev) => setExpected((x) => x.map((v, j) => (j === i ? ev.target.checked : v)))} />
                <span>
                  <Words text={e} />
                </span>
              </label>
            ))}
          </div>
          {t.answers && t.answers.length > 0 && (
            <>
              <h4 className="field-test-h">Write down</h4>
              <div className="field-test-answers">
                {t.answers.map((a) => (
                  <div key={a.key} className="field">
                    <label htmlFor={`ft-${t.id}-${a.key}`}>
                      <Words text={a.label} />
                    </label>
                    {a.options ? (
                      <select
                        id={`ft-${t.id}-${a.key}`}
                        className="input"
                        value={answers[a.key] ?? ''}
                        disabled={busy}
                        onChange={(e) => setAnswers((x) => ({ ...x, [a.key]: e.target.value }))}
                      >
                        <option value="">Choose…</option>
                        {a.options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`ft-${t.id}-${a.key}`}
                        className="input"
                        maxLength={1000}
                        value={answers[a.key] ?? ''}
                        disabled={busy}
                        onChange={(e) => setAnswers((x) => ({ ...x, [a.key]: e.target.value }))}
                      />
                    )}
                    {a.hint && <small className="muted">{a.hint}</small>}
                  </div>
                ))}
              </div>
            </>
          )}
          <h4 className="field-test-h">Screenshots</h4>
          <div
            className={`field-test-drop${dropping ? ' over' : ''}`}
            tabIndex={0}
            role="group"
            aria-label={`Screenshots for this result: paste, drop or choose`}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes('Files')) return;
              e.preventDefault();
              setDropping(true);
            }}
            onDragLeave={() => setDropping(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDropping(false);
              void addPictures(picturesIn(e.dataTransfer));
            }}
          >
            {shots.length > 0 && (
              <ul className="field-test-shots">
                {shots.map((x) => (
                  <li key={x.id}>
                    <a href={x.url} target="_blank" rel="noopener noreferrer">
                      <img src={x.url} alt={x.name || 'Screenshot'} loading="lazy" />
                    </a>
                    <button type="button" className="link-btn small-print" onClick={() => void removeShot(x.id)} aria-label={`Remove ${x.name || 'this screenshot'}`}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span className="muted small-print">
              {adding > 0 ? `Adding ${adding} screenshot${adding === 1 ? '' : 's'}…` : 'Paste a screenshot here (⌘V or Ctrl+V), drop one in, or'}{' '}
              {adding === 0 && (
                <button type="button" className="link-btn small-print" disabled={busy} onClick={() => pickNew.current?.click()}>
                  choose files
                </button>
              )}
              {adding === 0 && '. They’re kept with this result when you save it.'}
            </span>
            <input
              ref={pickNew}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              hidden
              onChange={(e) => {
                void addPictures([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </div>
          {r && (
            <div className="field-test-saved-shots">
              {r.shots && r.shots.length > 0 && (
                <ul className="field-test-shots" aria-label="Screenshots with the newest result">
                  {r.shots.map((x) => (
                    <li key={x.id}>
                      <a href={x.url} target="_blank" rel="noopener noreferrer">
                        <img src={x.url} alt={x.name || 'Screenshot'} loading="lazy" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <small className="muted">
                {r.shots?.length ? `${r.shots.length} with the result saved ${shortWhen(r.testedAt)}. ` : `None with the result saved ${shortWhen(r.testedAt)}. `}
                <button type="button" className="link-btn small-print" disabled={adding > 0} onClick={() => pickSaved.current?.click()}>
                  Add one to that result
                </button>
              </small>
              <input
                ref={pickSaved}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                hidden
                onChange={(e) => {
                  void addPictures([...(e.target.files ?? [])], 'saved');
                  e.target.value = '';
                }}
              />
            </div>
          )}
          {shotError && <Notice tone="crit">{shotError}</Notice>}
          <div className="field stretch">
            <label htmlFor={`ft-${t.id}-notes`}>Notes</label>
            <textarea id={`ft-${t.id}-notes`} className="input textarea" rows={3} maxLength={8000} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} />
            <small className="muted">What you saw, error messages word for word. No passwords, keys, or whole phone numbers (the last four digits are fine).</small>
          </div>
          {t.ifFails && (
            <p className="small-print">
              <strong>If it doesn’t work:</strong> <Words text={t.ifFails} />
            </p>
          )}
          {error && <Notice tone="crit">{error}</Notice>}
          <div className="wizard-actions">
            <Button size="small" icon="check" disabled={busy} onClick={() => void save('passed')}>
              Passed
            </Button>
            <Button size="small" variant="danger" icon="x" disabled={busy} onClick={() => void save('failed')}>
              Failed
            </Button>
            <Button size="small" variant="ghost" disabled={busy} onClick={() => void save('blocked')}>
              Couldn’t try
            </Button>
            <Button size="small" variant="ghost" disabled={busy} onClick={() => void save('skipped')}>
              Skip
            </Button>
            {r && (
              <Button size="small" variant="ghost" disabled={busy} onClick={() => void save('not_run')}>
                Not done
              </Button>
            )}
          </div>
          {!t.ready && <p className="small-print muted">This needs Stitch {t.needs}. You can still record it if you tried it somewhere newer.</p>}
          {t.results > 0 && (
            <div className="field-test-history">
              {history === null ? (
                <button type="button" className="link-btn small-print" onClick={() => void loadHistory()}>
                  {t.results === 1 ? 'Show the result recorded' : `Show all ${t.results} results`}
                </button>
              ) : (
                <ul className="plain-list">
                  {history.map((h) => (
                    <li key={h.id}>
                      <Chip small tone={STATUS[h.status].tone}>
                        {STATUS[h.status].label}
                      </Chip>
                      <small className="muted">
                        {h.testedBy} · {shortWhen(h.testedAt)} · {h.build}
                        {h.shots?.length ? ` · ${h.shots.length} screenshot${h.shots.length === 1 ? '' : 's'}` : ''}
                        {h.notes ? ` · ${h.notes.slice(0, 160)}` : ''}
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
